import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, CircularProgress, Alert, Menu, MenuItem } from '@mui/material';
import { Add as AddIcon, MoreVert as MoreVertIcon } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getProducts, ApiProduct, deleteProduct, reorderProducts } from '../../api/productsService';
import { deleteSection } from '../../api/sectionsService';
import ProductCard, { Product as ProductCardProduct } from '../../components/ProductCard/ProductCard';
import { CustomTypography, CustomButton } from '../../components/shared';
import { DESIGN_TOKENS } from '../../styles/constants';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useVendorAuth } from '../../contexts/VendorAuthContext';
import AddSectionPopup from '../../components/AddSectionPopup/AddSectionPopup';
import EditSectionPopup, { type SectionForEdit } from '../../components/EditSectionPopup/EditSectionPopup';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import type { SectionLocale } from '../../api/productsService';

interface Product extends ProductCardProduct {}

interface ProductSectionWithTransformedProducts {
  sectionId: string;
  name: string;
  displayOrder: number;
  products: Product[];
  locale?: SectionLocale;
}

// Helper function to normalize status from API format to component format
const normalizeStatus = (status: string): 'active' | 'suspended' | 'under_review' | 'draft' | 'deactivated' => {
  const normalized = status.toLowerCase();
  if (normalized === 'active') return 'active';
  if (normalized === 'suspended') return 'suspended';
  if (normalized === 'inreview' || normalized === 'under_review' || normalized === 'under review') return 'under_review';
  if (normalized === 'draft') return 'draft';
  if (normalized === 'deactivated') return 'deactivated';
  return 'draft'; // default fallback
};

const formatCurrency = (amount: number): string => {
  if (!Number.isFinite(amount)) {
    return 'QAR 0.00';
  }

  return `QAR ${amount.toFixed(2)}`;
};

const getImagePath = (imageUrl: string | undefined): string => {
  // Return empty string if image URL is missing or empty - ProductCard will show neutral placeholder
  if (!imageUrl || imageUrl.trim() === '') {
    return '';
  }

  // Return valid HTTP/HTTPS URLs as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // For relative paths, return as is (they will be handled by the image error handler if they fail)
    return imageUrl;
};

const transformProduct = (apiProduct: ApiProduct): Product & { sectionId?: string; orderInSection?: string } => ({
  id: apiProduct.productId,
  name: apiProduct.title,
  price: formatCurrency(apiProduct.amount ?? 0),
  description: apiProduct.shortDescription || apiProduct.fullDescription || '',
  image: getImagePath(apiProduct.imageURLs?.[0]), // Use first image as primary
  status: normalizeStatus(apiProduct.status),
  sectionId: apiProduct.sectionId,
  orderInSection: apiProduct.orderInSection,
});

// Products are now already grouped by sections from the API, so no need for client-side grouping

const Products: React.FC = () => {
  usePageTitle('Products');
  const navigate = useNavigate();
  const { vendor: sessionVendor } = useVendorAuth();

  const [productSections, setProductSections] = useState<ProductSectionWithTransformedProducts[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isAddSectionPopupOpen, setIsAddSectionPopupOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteSectionConfirmOpen, setDeleteSectionConfirmOpen] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<{ sectionId: string; name: string } | null>(null);
  const [isDeletingSection, setIsDeletingSection] = useState(false);
  const [sectionMenuAnchorEl, setSectionMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSection, setSelectedSection] = useState<{ sectionId: string; name: string } | null>(null);
  const [isEditSectionPopupOpen, setIsEditSectionPopupOpen] = useState(false);
  const [sectionToEdit, setSectionToEdit] = useState<SectionForEdit | null>(null);

  const fetchData = useCallback(async () => {
    if (!sessionVendor?.vendorId) {
      setError('Vendor session not found');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch products (already grouped by sections from API)
      const productsResponse = await getProducts(sessionVendor.vendorId);

      const sections = productsResponse.sections ?? [];

      // Transform products within each section (include locale for edit)
      const transformedSections: ProductSectionWithTransformedProducts[] = sections.map(section => ({
        sectionId: section.sectionId,
        name: section.name,
        displayOrder: section.displayOrder,
        products: section.products.map(transformProduct),
        ...(section.locale && { locale: section.locale }),
      }));

      setProductSections(transformedSections);
    } catch (err) {
      let message = 'Unable to load products right now.';

      if (axios.isAxiosError(err)) {
        message = err.response?.data?.message ?? err.message ?? message;
      } else if (err instanceof Error) {
        message = err.message;
      }

      setError(message);
      setProductSections([]);
    } finally {
      setIsLoading(false);
    }
  }, [sessionVendor?.vendorId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddSectionClick = () => {
    setIsAddSectionPopupOpen(true);
  };

  const handleSectionCreated = () => {
    fetchData();
  };

  const handleDeleteClick = (productId: string) => {
    setProductToDelete(productId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;

    setIsDeleting(true);
    try {
      await deleteProduct(productToDelete);
      toast.success('Product deleted successfully');
      setDeleteConfirmOpen(false);
      setProductToDelete(null);
      // Refresh the products list
      fetchData();
    } catch (err) {
      let message = 'Failed to delete product. Please try again.';
      
      if (axios.isAxiosError(err)) {
        const responseData = err.response?.data;
        const errorField = responseData?.error;
        const messageField = responseData?.message;
        
        // Combine error and message fields if both exist
        if (errorField && messageField && errorField !== messageField) {
          message = `${errorField} - ${messageField}`;
        } else if (errorField) {
          message = errorField;
        } else if (messageField) {
          message = messageField;
        } else {
          message = err.message || message;
        }
      } else if (err instanceof Error) {
        message = err.message;
      }
      
      toast.error(message);
      // Close dialog even on error
      setDeleteConfirmOpen(false);
      setProductToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setProductToDelete(null);
  };

  const handleSectionMenuOpen = (event: React.MouseEvent<HTMLElement>, section: ProductSectionWithTransformedProducts) => {
    event.stopPropagation();
    setSectionMenuAnchorEl(event.currentTarget);
    setSelectedSection({ sectionId: section.sectionId, name: section.name });
    setSectionToEdit({
      sectionId: section.sectionId,
      name: section.name,
      displayOrder: section.displayOrder,
      ...(section.locale && { locale: section.locale }),
    });
  };

  const handleSectionMenuClose = () => {
    setSectionMenuAnchorEl(null);
    setSelectedSection(null);
  };

  const handleEditSectionClick = () => {
    if (sectionToEdit) setIsEditSectionPopupOpen(true);
    handleSectionMenuClose();
  };

  const handleDeleteSectionClick = () => {
    if (selectedSection) {
      setSectionToDelete(selectedSection);
      setDeleteSectionConfirmOpen(true);
    }
    handleSectionMenuClose();
  };

  const handleDeleteSectionConfirm = async () => {
    if (!sectionToDelete) return;

    setIsDeletingSection(true);
    try {
      await deleteSection(sectionToDelete.sectionId);
      toast.success('Section deleted successfully');
      setDeleteSectionConfirmOpen(false);
      setSectionToDelete(null);
      // Refresh the products list
      fetchData();
    } catch (err) {
      let message = 'Failed to delete section. Please try again.';
      
      if (axios.isAxiosError(err)) {
        const responseData = err.response?.data;
        const errorField = responseData?.error;
        const messageField = responseData?.message;
        
        // Combine error and message fields if both exist
        if (errorField && messageField && errorField !== messageField) {
          message = `${errorField} - ${messageField}`;
        } else if (errorField) {
          message = errorField;
        } else if (messageField) {
          message = messageField;
        } else {
          message = err.message || message;
        }
      } else if (err instanceof Error) {
        message = err.message;
      }
      
      toast.error(message);
      // Close dialog even on error
      setDeleteSectionConfirmOpen(false);
      setSectionToDelete(null);
    } finally {
      setIsDeletingSection(false);
    }
  };

  const handleDeleteSectionCancel = () => {
    setDeleteSectionConfirmOpen(false);
    setSectionToDelete(null);
  };

  const handleProductDragEnd = async (result: DropResult) => {
    const { destination, source } = result;
    if (!destination) return;

    // Distinct Droppable `type` per section already blocks cross-section drops;
    // keep an explicit guard as well.
    if (destination.droppableId !== source.droppableId) return;
    if (destination.index === source.index) return;

    const sectionId = source.droppableId;
    const vendorId = sessionVendor?.vendorId;
    if (!vendorId) return;

    const sectionIndex = productSections.findIndex((s) => s.sectionId === sectionId);
    if (sectionIndex < 0) return;

    const section = productSections[sectionIndex];
    const previousProducts = section.products;
    const reordered = Array.from(previousProducts);
    const [moved] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, moved);

    const nextSections = productSections.map((s, i) =>
      i === sectionIndex ? { ...s, products: reordered } : s,
    );
    setProductSections(nextSections);

    try {
      await reorderProducts(
        vendorId,
        reordered.map((product, index) => ({
          productId: product.id,
          sectionId,
          displayOrder: index + 1,
        })),
      );
    } catch (err) {
      // Roll back optimistic reorder on failure
      setProductSections((current) =>
        current.map((s) =>
          s.sectionId === sectionId ? { ...s, products: previousProducts } : s,
        ),
      );

      let message = 'Failed to save product order. Please try again.';
      if (axios.isAxiosError(err)) {
        message = err.response?.data?.error ?? err.response?.data?.message ?? err.message ?? message;
      } else if (err instanceof Error) {
        message = err.message;
      }
      toast.error(message);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        fontFamily: DESIGN_TOKENS.typography.fontFamily,
        backgroundColor: DESIGN_TOKENS.colors.background,
      }}
    >
      <Box
        component="main"
        sx={{
          bgcolor: DESIGN_TOKENS.colors.background,
          p: 0,
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: 10, py: 5 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              mb: 3,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: DESIGN_TOKENS.typography.fontSizes.h1,
                  fontWeight: DESIGN_TOKENS.typography.fontWeights.bold,
                  color: DESIGN_TOKENS.colors.text,
                  lineHeight: 1.15,
                  letterSpacing: '-0.44px',
                  mb: 1,
                }}
              >
                Products
              </Typography>
              <Typography
                sx={{
                  fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
                  fontWeight: DESIGN_TOKENS.typography.fontWeights.normal,
                  color: DESIGN_TOKENS.colors.text,
                  lineHeight: 1.4,
                }}
              >
                Manage the products you offer through Eventi
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <CustomButton 
                variant="primary" 
                startIcon={<AddIcon />} 
                size="large"
                onClick={() => navigate('/products/new')}
              >
                Add product
              </CustomButton>

              <CustomButton 
                variant="primary" 
                startIcon={<AddIcon />} 
                size="large"
                onClick={handleAddSectionClick}
              >
                Add section
              </CustomButton>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            px: 10,
            pb: 5,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {isLoading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 10,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {!isLoading && error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {!isLoading && !error && productSections.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 10,
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: DESIGN_TOKENS.typography.fontSizes.h3,
                  fontWeight: DESIGN_TOKENS.typography.fontWeights.medium,
                  color: DESIGN_TOKENS.colors.text,
                  mb: 2,
                }}
              >
                No products yet
              </Typography>
              <Typography
                sx={{
                  fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
                  color: DESIGN_TOKENS.colors.text,
                  opacity: 0.7,
                }}
              >
                Start by adding your first product
              </Typography>
            </Box>
          )}

          {!isLoading && (
            <DragDropContext onDragEnd={handleProductDragEnd}>
              {productSections.map((section) => (
                <Box key={section.sectionId} sx={{ mb: 6 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.25,
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <CustomTypography 
                      variant="h2" 
                      weight="bold"
                      sx={{ 
                        flex: 1,
                        fontWeight: DESIGN_TOKENS.typography.fontWeights.bold,
                      }}
                    >
                      {section.name}
                      {section.locale?.ar?.name && (
                        <Box component="span" sx={{ fontWeight: 500, opacity: 0.85 }}>
                          {' '}({section.locale.ar.name})
                        </Box>
                      )}
                    </CustomTypography>
                    <IconButton
                      onClick={(e) => handleSectionMenuOpen(e, section)}
                      sx={{
                        width: '32px',
                        height: '32px',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <MoreVertIcon sx={{ fontSize: '20px', color: '#181818' }} />
                    </IconButton>
                    <Menu
                      anchorEl={sectionMenuAnchorEl}
                      open={Boolean(sectionMenuAnchorEl) && selectedSection?.sectionId === section.sectionId}
                      onClose={handleSectionMenuClose}
                      PaperProps={{
                        sx: {
                          borderRadius: '12px',
                          mt: 1,
                          minWidth: 150,
                          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        },
                      }}
                    >
                      <MenuItem
                        onClick={handleEditSectionClick}
                        sx={{
                          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                          fontSize: '16px',
                          color: '#181818',
                        }}
                      >
                        Edit
                      </MenuItem>
                      <MenuItem
                        onClick={handleDeleteSectionClick}
                        sx={{
                          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                          fontSize: '16px',
                          color: '#d32f2f',
                          '&:hover': {
                            backgroundColor: 'rgba(211, 47, 47, 0.08)',
                          },
                        }}
                      >
                        Delete
                      </MenuItem>
                    </Menu>
                  </Box>

                  {section.products.length === 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 8,
                        textAlign: 'center',
                        borderRadius: '16px',
                        backgroundColor: DESIGN_TOKENS.colors.background,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: DESIGN_TOKENS.typography.fontSizes.body1,
                          color: DESIGN_TOKENS.colors.text,
                          opacity: 0.7,
                          mb: 3,
                        }}
                      >
                        No products in this section yet
                      </Typography>
                      <CustomButton 
                        variant="primary" 
                        startIcon={<AddIcon />} 
                        size="large"
                        onClick={() => navigate(`/products/new?sectionId=${section.sectionId}`)}
                      >
                        Add product
                      </CustomButton>
                    </Box>
                  ) : (
                    <Droppable
                      droppableId={section.sectionId}
                      type={`section-products-${section.sectionId}`}
                    >
                      {(provided) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          sx={{
                            display: 'grid',
                            width: '100%',
                            gridTemplateColumns: {
                              xs: 'repeat(2, 1fr)',
                              sm: 'repeat(3, 1fr)',
                              md: 'repeat(4, 1fr)',
                              lg: 'repeat(5, 1fr)',
                              xl: 'repeat(6, 1fr)',
                            },
                            gap: 4,
                            alignItems: 'start',
                            boxSizing: 'border-box',
                          }}
                        >
                          {section.products.map((product, index) => (
                            <Draggable key={product.id} draggableId={product.id} index={index}>
                              {(dragProvided, snapshot) => (
                                <Box
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  sx={{
                                    ...dragProvided.draggableProps.style,
                                  }}
                                >
                                  <ProductCard
                                    product={product}
                                    onDelete={handleDeleteClick}
                                    dragHandleProps={dragProvided.dragHandleProps}
                                    isDragging={snapshot.isDragging}
                                  />
                                </Box>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </Box>
                      )}
                    </Droppable>
                  )}
                </Box>
              ))}
            </DragDropContext>
          )}
        </Box>
      </Box>
      
      {sessionVendor?.vendorId && (
        <>
          <AddSectionPopup
            open={isAddSectionPopupOpen}
            onClose={() => setIsAddSectionPopupOpen(false)}
            onSuccess={handleSectionCreated}
            vendorId={sessionVendor.vendorId}
          />
          <EditSectionPopup
            open={isEditSectionPopupOpen}
            onClose={() => { setIsEditSectionPopupOpen(false); setSectionToEdit(null); }}
            onSuccess={handleSectionCreated}
            vendorId={sessionVendor.vendorId}
            section={sectionToEdit}
          />
        </>
      )}

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Product"
        message={`Are you sure you want to delete this product? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
        isLoading={isDeleting}
      />

      <ConfirmDialog
        open={deleteSectionConfirmOpen}
        onClose={handleDeleteSectionCancel}
        onConfirm={handleDeleteSectionConfirm}
        title="Delete Section"
        message={`Are you sure you want to delete the section "${sectionToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
        isLoading={isDeletingSection}
      />
    </Box>
  );
};

export default Products;
